import { FieldComponent } from 'src/app/common/components/field/field.component';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
  NgZone,
  OnDestroy
} from '@angular/core';
import { GoogleMapService } from 'src/app/modules/shared/services/google.map.service';

@Component({
  selector: 'ig-address-input',
  templateUrl: './address.input.component.html',
  styleUrls: ['./address.input.component.scss'],
})
export class AddressInputComponent implements AfterViewInit, OnDestroy {
  googleLoaded$: Observable<boolean>;
  autoComplete: google.maps.places.Autocomplete;
  autocompleteListener: google.maps.MapsEventListener;

  @Input() label: string = 'Location / Address * (or check Virtual Events)';
  @Input() hint: string =
    'Start typing the physical address and select from a verified Google Maps address below';
  @Input() control: FormControl;

  @ViewChild('inputElement', { static: true }) inputElement: FieldComponent;


  constructor(
    public googleMapService: GoogleMapService,
    private ngZone: NgZone
  ) {
    try {
      this.googleLoaded$ = this.googleMapService.loadGoogleMapApi();
    } catch (e) {
      console.error(e);
    }
  }

  ngAfterViewInit() {
    try {
      
      this.googleLoaded$.subscribe((isLoaded: boolean) => {
        if (!isLoaded) return;
        
        // Initialize autocomplete with specific options
        this.initializeAutocomplete();
      });
    } catch (e) {
      console.error(e);
    }
  }

  ngOnDestroy() {
    // Clean up event listeners
    if (this.autocompleteListener) {
      google.maps.event.removeListener(this.autocompleteListener);
    }
  }

  initializeAutocomplete() {
    // Create the autocomplete object
    this.autoComplete = new google.maps.places.Autocomplete(
      this.inputElement.input.nativeElement,
      {
        fields: [
          'address_components',
          'geometry',
          'formatted_address',
          'name',
          'place_id',
        ],
      }
    );
      
    // Prevent zoom-in on iOS when input is focused
    this.inputElement.input.nativeElement.style.fontSize = '16px';
    
    // Add touchend event listener for iOS
    this.inputElement.input.nativeElement.addEventListener('touchend', () => {
      // Small delay to ensure the field is properly focused
      setTimeout(() => {
        // Force keyboard to appear on iOS
        this.inputElement.input.nativeElement.setAttribute('readonly', 'false');
        this.inputElement.input.nativeElement.blur();
        this.inputElement.input.nativeElement.focus();
      }, 10);
    });
    
    // ***NEW CODE - FIX FOR PAC-CONTAINER***
    // Monitor DOM for pac-container and apply fixes when it appears
    this.addPacContainerFix();

    // Add place_changed event listener
    this.autocompleteListener = this.autoComplete.addListener('place_changed', () => {
      // Use NgZone to ensure Angular detects the changes
      this.ngZone.run(() => {
        const place = this.autoComplete.getPlace();
        this.handlePlaceSelection(place);
      });
    });
  }

  // New method to fix PAC container issues on iOS
  addPacContainerFix() {
    // Create a mutation observer to detect when Google adds the pac-container to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Look for pac-container in the document
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer) {
            // Add z-index to ensure it's visible
            (pacContainer as HTMLElement).style.zIndex = '1000';
            
            // Prevent touchend events from bubbling up
            pacContainer.addEventListener('touchend', (e) => {
              e.stopImmediatePropagation();
            }, true);
            
            // Apply needsclick to all pac-items to prevent fastclick issues
            const pacItems = pacContainer.querySelectorAll('.pac-item, .pac-item span');
            pacItems.forEach((item) => {
              item.classList.add('needsclick');
            });
            
            // Once we've found and fixed the pac-container, disconnect the observer
            observer.disconnect();
          }
        }
      });
    });
    
    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  handlePlaceSelection(place: google.maps.places.PlaceResult) {
    if (!place || !place.formatted_address) {
      console.log('No place details available', place);
      return;
    }

    if (place.geometry) {
      let stateAbbr = '',
        cityName = '',
        country = '';
        
      if (place.address_components) {
        place.address_components.forEach((x) => {
          if (x.types.includes('administrative_area_level_1')) {
            stateAbbr = x.short_name;
          }
          if (x.types.includes('locality')) {
            cityName = x.long_name;
          }
          if (x.types.includes('country')) {
            country = x.long_name;
          }
        });
      }
      
      this.control.setValue({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        address: place.formatted_address,
        address_components: place.address_components,
        placeId: place.place_id,
        cityName,
        stateAbbr,
        country,
      });
      this.inputElement.input.nativeElement.value = place.formatted_address;
    } else {
      this.control.setValue({
        address: place.name,
      });
      this.inputElement.input.nativeElement.value = place.name;
    }
  }

  clear() {
    this.control.setValue(null);
    this.inputElement.input.nativeElement.value = '';
  }
}